import { connectionManagementService } from '../../../services/connectionManagementService';
import { dataCache } from '../../../utils/dataCache';
import type {
  Collection,
  UseCase,
  CreateCollectionInput,
  UpdateCollectionInput,
  CreateSubscriptionInput,
  CancelSubscriptionInput,
} from '@amzn/aws-gcr-content-connection-management-client';

// Create a service factory that takes region as parameter
export const createPortalService = (region: string) => ({
  async getAllCollections(): Promise<Collection[]> {
    const cached = dataCache.get<Collection[]>('collections', region);
    if (cached) return cached;

    const apiCollections = await connectionManagementService.listCollections();
    dataCache.set('collections', apiCollections, region);
    return apiCollections;
  },

  async getAllApplications(): Promise<UseCase[]> {
    const cached = dataCache.get<UseCase[]>('applications', region);
    if (cached) return cached;

    const apiUseCases = await connectionManagementService.listUseCases();
    dataCache.set('applications', apiUseCases, region);
    return apiUseCases;
  },

  async getCollectionDetail(collectionId: string): Promise<Collection> {
    return await connectionManagementService.getCollectionDetail({ collectionId });
  },

  async createCollection(data: CreateCollectionInput): Promise<Collection> {
    const result = await connectionManagementService.createCollection(data);
    dataCache.clearRegion(region);
    return result;
  },

  async updateCollection(data: UpdateCollectionInput): Promise<Collection> {
    const result = await connectionManagementService.updateCollection(data);
    dataCache.clearRegion(region);
    return result;
  },

  async getApplicationDetail(applicationId: string): Promise<UseCase> {
    return await connectionManagementService.getUseCaseDetail({ useCaseId: applicationId });
  },

  async subscribeToCollection(data: CreateSubscriptionInput) {
    const result = await connectionManagementService.createSubscription(data);
    dataCache.clearRegion(region);
    return result;
  },

  async unsubscribeFromCollection(data: CancelSubscriptionInput) {
    const result = await connectionManagementService.cancelSubscription(data);
    dataCache.clearRegion(region);
    return result;
  },
});

// Global service that uses current region from localStorage
const getPortalService = () => {
  const getCurrentRegion = () => {
    try {
      return localStorage.getItem('selectedRegion') === 'china' ? 'china' : 'global';
    } catch {
      return 'global';
    }
  };

  return createPortalService(getCurrentRegion());
};

export const portalService = {
  getAllCollections: () => getPortalService().getAllCollections(),
  getAllApplications: () => getPortalService().getAllApplications(),
  getCollectionDetail: (id: string) => getPortalService().getCollectionDetail(id),
  createCollection: (data: CreateCollectionInput) => getPortalService().createCollection(data),
  updateCollection: (data: UpdateCollectionInput) => getPortalService().updateCollection(data),
  getApplicationDetail: (id: string) => getPortalService().getApplicationDetail(id),
  subscribeToCollection: (data: CreateSubscriptionInput) => getPortalService().subscribeToCollection(data),
  unsubscribeFromCollection: (data: CancelSubscriptionInput) => getPortalService().unsubscribeFromCollection(data),
};
